import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, isValid, parseISO } from 'date-fns';
import ReactMarkdown from 'react-markdown';

function NoteList({ notes, categories, onAddNote, onEditNote, onDeleteNote }) {
  const [selected, setSelected] = React.useState(null);
  const [showSubscribe, setShowSubscribe] = React.useState(false);

  const handleAddClick = () => {
    const hasSeenPopup10 = localStorage.getItem('seenPopup10') === 'true';
    const hasSeenPopup100 = localStorage.getItem('seenPopup100') === 'true';

    if (notes.length === 10 && !hasSeenPopup10) {
      setShowSubscribe(true);
      return;
    }

    if (notes.length === 100 && !hasSeenPopup100) {
      setShowSubscribe(true);
      return;
    }

    onAddNote();
  };

  const handleMaybeLater = () => {
    if (notes.length === 10) {
      localStorage.setItem('seenPopup10', 'true');
    } else if (notes.length === 100) {
      localStorage.setItem('seenPopup100', 'true');
    }
    setShowSubscribe(false);
    onAddNote();
  };

  const handleSubscribe = () => {
    if (notes.length === 10) {
      localStorage.setItem('seenPopup10', 'true');
    } else if (notes.length === 100) {
      localStorage.setItem('seenPopup100', 'true');
    }
    window.open('https://example.com/subscribe', '_blank');
    setShowSubscribe(false);
    onAddNote();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = parseISO(timestamp);
    return isValid(date) ? format(date, 'MMM d, yyyy') : '';
  };

  return (
    <>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Fab color="primary" aria-label="add" onClick={handleAddClick}>
            <AddIcon />
          </Fab>
        </Box>
      </Grid>
      {notes.map((note) => {
        const category = categories.find((cat) => cat.id === note.categoryId);
        const isSelected = selected === note.id;
        return (
          <Grid item xs={12} sm={6} md={4} key={note.id}>
            <Card
              sx={{
                height: '100%',
                minHeight: '200px',
                maxHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
                borderLeft: 6,
                borderColor: category?.color || 'grey.300',
                backgroundColor: isSelected
                  ? alpha(category?.color || '#000', 0.1)
                  : 'background.paper',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                  backgroundColor: alpha(category?.color || '#000', 0.05),
                },
              }}
              onClick={() => setSelected(isSelected ? null : note.id)}
            >
              <CardContent
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Created: {formatDate(note.timestamp)}
                    </Typography>
                    {note.lastEdited && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Edited: {formatDate(note.lastEdited)}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Tooltip title="Edit note">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditNote(note);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete note">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteNote(note.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    mb: 1,
                  }}
                >
                  <ReactMarkdown>{note.content}</ReactMarkdown>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: alpha(category?.color || '#000', 0.1),
                    color: 'text.secondary',
                  }}
                >
                  {category?.name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
      {notes.length === 0 && (
        <Grid item xs={12}>
          <Typography variant="body1" color="text.secondary" align="center">
            No notes found. Click the + button to create one!
          </Typography>
        </Grid>
      )}

      <Dialog open={showSubscribe} onClose={handleMaybeLater}>
        <DialogTitle>Support Us!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {notes.length === 10
              ? "You've created 10 notes! 🎉"
              : "Wow! You've created 100 notes! 🎉🎉"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Join our community to get updates about new features and tips.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMaybeLater}>Maybe Later</Button>
          <Button variant="contained" onClick={handleSubscribe}>
            Subscribe Now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default NoteList;
